import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { getImage } from '@utils/imageHelper';
import { Fonts, mediaQuery } from '@styles/index';
import TestSeriesListingTestCardTags from './TestSeriesListingTestCardTags';

export const TEST_SUBSCRIPTION_BADGE = {
  ICONIC: 'iconic',
  PLUS: 'plus',
  PRIME: 'prime',
  FREE: 'free'
};

export const TEST_LISTING_CARD_VARIANT = {
  QUIZ: 'quiz',
  TEST_SERIES: 'test_series'
};

const CONTENT_GRADIENT = `linear-gradient(
  197deg,
  var(--color-blue-bg) -41.92%,
  var(--color-base-light-0) 61.52%
)`;

const STATUS_TAG_CONFIG = {
  ATTEMPTED: { label: 'Attempted', color: 'var(--color-green-2)' },
  CANCELLED: { label: 'Cancelled', color: 'var(--color-i-red)' }
};

const SUBSCRIPTION_BADGE_CONFIG = {
  [TEST_SUBSCRIPTION_BADGE.ICONIC]: {
    label: 'Iconic',
    borderColor: 'var(--color-i-iconic-badge)',
    textColor: 'var(--color-i-iconic-badge)',
    icon: 'test-series-listing/cards/iconic-badge-icon.svg',
    iconSize: 12,
    isPrime: false
  },
  [TEST_SUBSCRIPTION_BADGE.PLUS]: {
    label: 'Plus',
    borderColor: 'var(--color-i-plus-badge)',
    textColor: 'var(--color-green-2)',
    icon: 'test-series-listing/cards/plus-badge-icon.svg',
    iconSize: 13,
    isPrime: false
  },
  [TEST_SUBSCRIPTION_BADGE.PRIME]: {
    label: 'Prime',
    borderColor: 'var(--color-i-prime-badge)',
    textColor: 'var(--color-i-prime-badge)',
    icon: 'test-series-listing/cards/prime-badge-icon.svg',
    iconSize: 12,
    isPrime: true
  },
  [TEST_SUBSCRIPTION_BADGE.FREE]: {
    label: 'Free',
    borderColor: 'var(--color-accent-blue)',
    textColor: 'var(--color-accent-blue)',
    isPrime: false
  }
};

const getSubscriptionBadgeKey = (subscriptionType, isPrimeEnabled) => {
  if (subscriptionType === TEST_SUBSCRIPTION_BADGE.PRIME && !isPrimeEnabled) {
    return TEST_SUBSCRIPTION_BADGE.FREE;
  }

  return subscriptionType;
};

const Card = styled.article`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${({ $fixedWidth }) => ($fixedWidth ? '273px' : '100%')};
  height: 209px;
  border-radius: 8px;
  background: var(--color-base-1);
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  overflow: hidden;

  ${({ $isLive, $isTestSeries }) => {
    if ($isTestSeries || $isLive) {
      return 'padding: 8px;';
    }

    return `
      gap: 24px;
      padding: 6px;
    `;
  }}

  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: auto;
    min-height: 179px;
  }
`;

const ContentBlock = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-radius: 4px;
  width: 100%;

  gap: 8px;
  padding: 12px 12px 16px;
  background: ${CONTENT_GRADIENT};
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const LiveRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LiveIndicatorIcon = styled.img`
  flex-shrink: 0;
`;

const LivePill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--color-i-red);
  font-size: 10px;
  line-height: 12px;
  font-weight: ${Fonts.BOLD};
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: var(--color-i-white);
`;

const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const LockBadge = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid var(--color-divider);
  border-radius: 34px;
  background: var(--color-base-1);
`;

const LockIcon = styled.img`
  flex-shrink: 0;
`;

const CategoryLabel = styled.span`
  font-size: 12px;
  line-height: 12px;
  font-weight: ${Fonts.BOLD};
  letter-spacing: 0.24px;
  text-transform: uppercase;
  color: var(--color-i-turquoise-4);
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;

  ${({ $isLive, $isTestSeries }) => {
    if ($isTestSeries) {
      return 'gap: 8px;';
    }

    if ($isLive) {
      return 'gap: 8px; flex: 1; min-height: 0; overflow: hidden;';
    }

    return 'gap: 6px; flex: 1; min-height: 0; overflow: hidden;';
  }}
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  line-height: 20px;
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  word-break: break-word;

  ${({ $isTestSeries }) =>
    $isTestSeries
      ? `
    flex-shrink: 0;
    max-height: 40px;
  `
      : ''}
`;

const Description = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  font-weight: ${Fonts.REGULAR};
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  word-break: break-word;

  ${({ $isTestSeries }) =>
    $isTestSeries
      ? `
    -webkit-line-clamp: 1;
    flex-shrink: 0;
    max-height: 18px;
  `
      : `
    -webkit-line-clamp: 2;
  `}
`;

const renderCategoryLabel = (categoryLabel, showLock) => {
  if (!showLock) {
    return <CategoryLabel>{categoryLabel}</CategoryLabel>;
  }

  return (
    <CategoryRow>
      <LockBadge aria-hidden>
        <LockIcon
          src={getImage('test-series-listing/cards/lock-icon.svg')}
          width={12}
          height={13.5}
          alt=""
        />
      </LockBadge>
      <CategoryLabel>{categoryLabel}</CategoryLabel>
    </CategoryRow>
  );
};

const TestSeriesListingTestCard = ({
  variant,
  categoryLabel,
  title,
  description,
  subscriptionType,
  showLock,
  statusTag,
  questionCount,
  durationMinutes,
  testCount,
  dateRangeLabel,
  isLive,
  href,
  onClick,
  useFixedWidth = false,
  isPrimeEnabled = true
}) => {
  const subscriptionBadge =
    SUBSCRIPTION_BADGE_CONFIG[
      getSubscriptionBadgeKey(subscriptionType, isPrimeEnabled)
    ];
  const statusBadge = STATUS_TAG_CONFIG[statusTag];
  const isClickable = Boolean(href || onClick);
  const isTestSeries = variant === TEST_LISTING_CARD_VARIANT.TEST_SERIES;

  const handleKeyDown = (event) => {
    if (!isClickable || !onClick) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      $fixedWidth={useFixedWidth}
      $isLive={isLive}
      $isTestSeries={isTestSeries}
      $isClickable={isClickable}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <ContentBlock>
        {isLive ? (
          <HeaderGroup>
            <LiveRow>
              <LiveIndicatorIcon
                src={getImage(
                  'test-series-listing/cards/live-indicator-icon.svg'
                )}
                width={12}
                height={12}
                alt=""
              />
              <LivePill>LIVE</LivePill>
            </LiveRow>
            {renderCategoryLabel(categoryLabel, showLock)}
          </HeaderGroup>
        ) : (
          renderCategoryLabel(categoryLabel, showLock)
        )}
        <TextGroup $isLive={isLive} $isTestSeries={isTestSeries}>
          <Title $isTestSeries={isTestSeries}>{title}</Title>
          {description ? (
            <Description $isTestSeries={isTestSeries}>
              {description}
            </Description>
          ) : null}
        </TextGroup>
      </ContentBlock>
      <TestSeriesListingTestCardTags
        isTestSeries={isTestSeries}
        subscriptionBadge={subscriptionBadge}
        statusBadge={statusBadge}
        questionCount={questionCount}
        durationMinutes={durationMinutes}
        testCount={testCount}
        dateRangeLabel={dateRangeLabel}
      />
    </Card>
  );
};

export default TestSeriesListingTestCard;

TestSeriesListingTestCard.propTypes = {
  variant: PropTypes.oneOf(Object.values(TEST_LISTING_CARD_VARIANT)),
  categoryLabel: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  subscriptionType: PropTypes.oneOf(Object.values(TEST_SUBSCRIPTION_BADGE))
    .isRequired,
  showLock: PropTypes.bool,
  statusTag: PropTypes.oneOf(['ATTEMPTED', 'CANCELLED']),
  questionCount: PropTypes.number,
  durationMinutes: PropTypes.number,
  testCount: PropTypes.number,
  dateRangeLabel: PropTypes.string,
  isLive: PropTypes.bool,
  href: PropTypes.string,
  onClick: PropTypes.func,
  useFixedWidth: PropTypes.bool,
  isPrimeEnabled: PropTypes.bool
};

TestSeriesListingTestCard.defaultProps = {
  variant: TEST_LISTING_CARD_VARIANT.QUIZ,
  description: '',
  showLock: false,
  statusTag: null,
  questionCount: 0,
  durationMinutes: 0,
  testCount: 0,
  dateRangeLabel: '',
  isLive: false,
  href: undefined,
  onClick: undefined,
  useFixedWidth: false,
  isPrimeEnabled: true
};
